import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { triggerTextDownload, dateStamp } from '../../../services/downloadHelpers';

const DocumentViewer = ({ reports, onViewDocument }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Organize reports by category
  const documentCategories = {
    'compliance': reports?.filter(r => r?.type?.includes('directive') || r?.type?.includes('entgelttransparenz')),
    'assessments': reports?.filter(r => r?.type?.includes('assessment') || r?.type?.includes('equity')),
    'remediation': reports?.filter(r => r?.status === 'flagged' || r?.name?.toLowerCase()?.includes('remediation')),
    'verification': reports?.filter(r => r?.status === 'verified')
  };

  const getFilteredDocuments = () => {
    const docs = selectedCategory === 'all' ? reports : documentCategories?.[selectedCategory] || [];
    return docs?.filter(doc => 
      doc?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      doc?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );
  };

  const generateDocumentPreview = (report) => {
    // Mock document preview data
    return {
      summary: `This ${report?.type?.replace(/-/g, ' ')} report covers ${report?.dataPoints?.toLocaleString()} employee records across ${report?.department} for the period ${report?.period}. The report maintains ${report?.completeness}% data completeness and has been verified for integrity.`,
      keyMetrics: [
        { label: 'Pay Gap Analysis', value: '2.3% median gap', status: 'within-range' },
        { label: 'Data Coverage', value: `${report?.completeness}%`, status: 'excellent' },
        { label: 'Regulatory Compliance', value: 'Fully Compliant', status: 'compliant' },
        { label: 'Data Quality Score', value: '98.7/100', status: 'excellent' }
      ],
      sections: [
        'Executive Summary',
        'Methodology & Scope',
        'Data Analysis Results',
        'Pay Gap Measurements',
        'Regulatory Compliance Assessment',
        'Recommendations & Actions',
        'Appendix: Data Sources'
      ]
    };
  };

  const categories = [
    { id: 'all', label: 'All Documents', count: reports?.length },
    { id: 'compliance', label: 'Compliance Reports', count: documentCategories?.compliance?.length },
    { id: 'assessments', label: 'Joint Assessments', count: documentCategories?.assessments?.length },
    { id: 'remediation', label: 'Remediation Plans', count: documentCategories?.remediation?.length },
    { id: 'verification', label: 'Verified Reports', count: documentCategories?.verification?.length }
  ];

  const filteredDocs = getFilteredDocuments();

  return (
    <div className="space-y-6">
      {/* Document Categories */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {categories?.map(category => (
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                  selectedCategory === category?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                }`}
              >
                {category?.label} ({category?.count})
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Document Library */}
      <div className="grid gap-6">
        {filteredDocs?.map((document) => {
          const preview = generateDocumentPreview(document);
          
          return (
            <div key={document?.id} className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {document?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {preview?.summary}
                    </p>
                    
                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {preview?.keyMetrics?.map((metric, index) => (
                        <div key={index} className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                            {metric?.label}
                          </p>
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-semibold text-foreground">
                              {metric?.value}
                            </p>
                            {metric?.status === 'excellent' && (
                              <Icon name="CheckCircle" size={14} className="text-green-500" />
                            )}
                            {metric?.status === 'compliant' && (
                              <Icon name="Shield" size={14} className="text-blue-500" />
                            )}
                            {metric?.status === 'within-range' && (
                              <Icon name="TrendingUp" size={14} className="text-yellow-500" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={() => {
                        if (onViewDocument) {
                          onViewDocument(document);
                        } else {
                          // Fallback: show inline alert with summary
                          alert(`${document?.name}\n\n${preview?.summary}\n\nSections:\n${preview?.sections?.join('\n')}`);
                        }
                      }}
                      iconName="Eye"
                      iconPosition="left"
                    >
                      View Full Document
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => {
                        const safe = document?.name?.replace(/[^a-zA-Z0-9-_]/g, '_') || 'report';
                        const lines = [
                          '='.repeat(70),
                          document?.name || 'Document',
                          '='.repeat(70),
                          `Type: ${document?.type || 'Report'}`,
                          `Department: ${document?.department || 'N/A'}`,
                          `Period: ${document?.period || 'N/A'}`,
                          `Status: ${document?.status || 'N/A'}`,
                          `Data points: ${document?.dataPoints?.toLocaleString() || 'N/A'}`,
                          `Completeness: ${document?.completeness || 'N/A'}%`,
                          `Exported: ${dateStamp()} via ClearEqual`,
                          '='.repeat(70),
                          '',
                          'EXECUTIVE SUMMARY',
                          '-'.repeat(70),
                          preview?.summary || '',
                          '',
                          'KEY METRICS',
                          '-'.repeat(70),
                          ...(preview?.keyMetrics?.map(m => `  ${m.label.padEnd(30)} ${m.value}`) || []),
                          '',
                          'DOCUMENT SECTIONS',
                          '-'.repeat(70),
                          ...(preview?.sections?.map((s, i) => `  ${i + 1}. ${s}`) || []),
                          '',
                          '─'.repeat(70),
                          'This is a UAT export. Real PDFs require backend storage.',
                        ].join('\n');
                        triggerTextDownload(`ClearEqual_${safe}.txt`, lines);
                      }}
                    >
                      Download PDF
                    </Button>
                  </div>
                </div>

                {/* Document Sections */}
                <div className="border-t border-border pt-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3">
                    Document Sections
                  </h4>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                    {preview?.sections?.map((section, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <Icon name="FileText" size={12} className="text-muted-foreground" />
                        <span className="text-foreground">{section}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Document Metadata */}
                <div className="border-t border-border pt-4 mt-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>Created: {new Date(document?.createdAt)?.toLocaleDateString()}</span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Icon name="User" size={12} />
                      <span>By: {document?.submittedBy}</span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Icon name="HardDrive" size={12} />
                      <span>
                        Size: {(document?.fileSize / 1024 / 1024)?.toFixed(1)} MB
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <Icon name="Shield" size={12} />
                      <span>
                        Checksum: {document?.checksum?.substring(0, 12)}...
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {filteredDocs?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Documents Found</h3>
          <p className="text-muted-foreground">
            No documents match your current search criteria in the selected category.
          </p>
        </div>
      )}
      {/* Document Access Notice */}
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-blue-500 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">Document Access & Security</p>
            <p className="text-muted-foreground">
              All documents are provided in read-only format with verified checksums. 
              Export functionality generates tamper-evident copies with audit trails. 
              Access to sensitive compensation data requires additional verification steps.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;